import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, LogOut, CalendarDays, LayoutDashboard, ShieldCheck, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "./Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  ["Discover", "/services", "nav-discover"],
  ["Categories", "/categories", "nav-categories"],
  ["My Bookings", "/bookings", "nav-my-bookings"],
  ["List Your Business", "/list-your-business", "nav-list-business"],
];

const linkCls = ({ isActive }) =>
  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-sky-700 bg-sky-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`;

function AccountMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const initials = user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="account-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 hover:border-sky-300">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-xs font-bold text-white">{initials}</span>
        <span className="hidden text-sm font-medium text-slate-800 lg:inline">{user.name.split(" ")[0]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl">
        <DropdownMenuLabel>
          <p className="text-sm font-semibold">{user.name}</p>
          <p className="text-xs font-normal capitalize text-slate-500">{user.role} account</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-my-bookings" onClick={() => navigate("/bookings")}><CalendarDays className="mr-2 h-4 w-4" />My Bookings</DropdownMenuItem>
        {user.role === "provider" && (
          <DropdownMenuItem data-testid="menu-provider-dashboard" onClick={() => navigate("/provider-dashboard")}><LayoutDashboard className="mr-2 h-4 w-4" />Provider Dashboard</DropdownMenuItem>
        )}
        {user.role === "admin" && (
          <DropdownMenuItem data-testid="menu-admin-dashboard" onClick={() => navigate("/admin")}><ShieldCheck className="mr-2 h-4 w-4" />Admin Dashboard</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={onLogout}><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ user, onClose, onLogout }) {
  const items = [...LINKS];
  if (user?.role === "provider") items.push(["Provider Dashboard", "/provider-dashboard", "mnav-provider"]);
  if (user?.role === "admin") items.push(["Admin Dashboard", "/admin", "mnav-admin"]);
  return (
    <div className="border-t border-slate-200 bg-white px-4 pb-6 pt-2 md:hidden" data-testid="mobile-menu">
      {items.map(([label, to, id]) => (
        <NavLink key={to} to={to} onClick={onClose} data-testid={`m-${id}`} className="block rounded-xl px-3 py-3.5 text-base font-medium text-slate-700 hover:bg-slate-50">
          {label}
        </NavLink>
      ))}
      <div className="mt-3 grid gap-2">
        <Link to="/services" onClick={onClose} data-testid="m-find-service" className="flex h-12 items-center justify-center rounded-xl bg-sky-600 font-semibold text-white">Find a Service</Link>
        {user ? (
          <button data-testid="m-logout" onClick={() => { onLogout(); onClose(); }} className="h-12 rounded-xl border border-slate-300 font-semibold text-slate-700">Sign out</button>
        ) : (
          <Link to="/sign-in" onClick={onClose} data-testid="m-sign-in" className="flex h-12 items-center justify-center rounded-xl border border-slate-300 font-semibold text-slate-700">Sign In</Link>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/"); };
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          {LINKS.map(([label, to, id]) => <NavLink key={to} to={to} className={linkCls} data-testid={id}>{label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-3 md:flex">
            {!user && <Link to="/sign-in" data-testid="nav-sign-in" className="text-sm font-semibold text-slate-700 hover:text-sky-700">Sign In</Link>}
            <Link to="/services" data-testid="nav-find-service" className="inline-flex h-10 items-center gap-2 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-sky-700">
              <Search className="h-4 w-4" /> Find a Service
            </Link>
          </div>
          {user && <AccountMenu user={user} onLogout={handleLogout} />}
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 md:hidden" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu user={user} onClose={() => setOpen(false)} onLogout={handleLogout} />}
    </header>
  );
}

export { Store };
